import 'react-bootstrap-table-next/dist/react-bootstrap-table2.min.css'
import { useWindowWidth } from '@react-hook/window-size/throttled'
import classNames from 'classnames'
import { memo, useEffect, useMemo, useState } from 'react'
import Button from 'react-bootstrap/Button'
import BootstrapTable, { SelectRowProps } from 'react-bootstrap-table-next'
import ToolkitProvider, { CSVExport, Search } from 'react-bootstrap-table2-toolkit'
import { v4 } from 'uuid'

import CardHover from '@/components/CardHover/CardHover'
import Icon from '@/components/Icon/Icon'
import Mana from '@/components/Mana/Mana'
import Spinner from '@/components/Spinner/Spinner'
import cardService from '@/services/card.service'
import localStorageService from '@/services/localStorage.service'
import { update } from '@/store/cardlists/actions/update'
import { addCard } from '@/store/clipboard/actions/addCard'
import { REMOVE_CARD } from '@/store/clipboard/actions/types'
import { useActionless, useAppSelector } from '@/store/hooks'
import { CardLabelVariantType, CardListType, CardType, GenericCardType, RecCardType } from '@/types/card'
import { desktopWidth } from '@/utils/constants'
import { flatten } from '@/utils/helpers'

import styles from './TableView.module.scss'
import ToggleList from './ToggleList'

const { ExportCSVButton } = CSVExport
const { SearchBar } = Search

const cardFormatter = (cell: any, row: TableCardType, labelVariant?: CardLabelVariantType) =>
  cell && <CardHover card={row as GenericCardType} labelVariant={labelVariant} />

const colorIdentityFormatter = (cell: string, row: any) =>
  row.header ? <i>{row.header}</i> : <span className={styles.mana}>{<Mana colors={cell || ['c']} size={24} />}</span>

const cmcFormatter = (_cell: number, row: TableCardType) => <span className='float-right'>{row.cmc}</span>

const inclusionFormatter = (_cell: any, row: TableCardType) =>
  row.potential_decks && (
    <span className='float-right'>{`${((100 * row.num_decks) / row.potential_decks).toFixed(1)}%`}</span>
  )
const numDecksFormatter = (cell: number, _row: TableCardType) => <span className='float-right'>{cell}</span>

const priceCHFormatter = (_cell: any, row: TableCardType) => (
  <span className='float-right'>{row?.prices?.cardhoarder && `${row.prices.cardhoarder.price} TIX`}</span>
)

const priceCKFormatter = (_cell: any, row: TableCardType) => (
  <span className='float-right'>{row?.prices?.cardkingdom && `$${row.prices.cardkingdom.price.toFixed(2)}`}</span>
)

const priceF2FFormatter = (_cell: any, row: TableCardType) => (
  <span className='float-right'>{row?.prices?.face2face && `$${row.prices.face2face.price.toFixed(2)}`}</span>
)

const priceCMFormatter = (_cell: any, row: TableCardType) => (
  <span className='float-right'>
    {row?.prices?.cardmarket && `${row.prices.cardmarket.price.toFixed(2).replace('.', ',')} €`}
  </span>
)

const priceTCGPFormatter = (_cell: any, row: TableCardType) => (
  <span className='float-right'>{row?.prices?.tcgplayer && `$${row.prices.tcgplayer.price.toFixed(2)}`}</span>
)

const rarityFormatter = (_cell: string, row: TableCardType) => (
  <span className='float-right'>{row.rarity?.slice(0, 1).toUpperCase()}</span>
)

const saltFormatter = (_cell: number, row: TableCardType) => <span className='float-right'>{row.salt?.toFixed(2)}</span>

const synergyFormatter = (cell: number, _row: TableCardType) => {
  if (cell) {
    const num = Number((100 * cell).toFixed(0))
    const sign = num > 0 ? '+' : num < 0 ? '-' : ''
    return <span className='float-right'>{`${sign}${Math.abs(num)}%`}</span>
  }
}

const unpopularityFormatter = (cell: number) => cell && <span className='float-right'>{Math.round(100 * cell)}%</span>

const raritySort = (
  a: string,
  b: string,
  order: string,
  _dataField: string,
  _rowA: TableCardType,
  _rowB: TableCardType,
) => {
  const sortOrder = ['c', 'u', 'r', 'm', 's']
  const a2 = sortOrder.indexOf(a.slice(0, 1))
  const b2 = sortOrder.indexOf(b.slice(0, 1))
  return order === 'asc' ? b2 - a2 : a2 - b2
}

const reverseSort = (
  a: number,
  b: number,
  order: string,
  _dataField: string,
  _rowA: TableCardType,
  _rowB: TableCardType,
) => (order === 'asc' ? b - a : a - b)

type ColumnType = {
  classes?: string
  dataField: string
  formatter?: (cell: any, row: any) => React.ReactNode
  headerStyle?: React.CSSProperties
  hidden?: boolean
  sort?: boolean
  sortFunc?: (a: any, b: any, order: string, dataField: string, rowA: TableCardType, rowB: TableCardType) => number
  style?: React.CSSProperties
  text: string
}

type TableCardType = CardType & RecCardType & { key: string }

type Props = {
  cardLists: CardListType[]
  labelVariant?: CardLabelVariantType
  loading: boolean
}

const TableView = ({ cardLists, labelVariant, loading }: Props) => {
  const clipboardCards = useAppSelector((state) => state.clipboard.cards)
  const [removeCard] = useActionless(REMOVE_CARD)
  const [isRendered, setIsRendered] = useState(false)
  const [tableCards, setTableCards] = useState<Record<string, CardType>>({})
  const windowWidth = useWindowWidth()

  const isLoading = useMemo(() => {
    for (const cardlist of cardLists) {
      for (const cardview of cardlist.cardviews) {
        if (cardview.cards) {
          if (cardview.cards.filter((card) => !tableCards[card.url]).length) return true
        } else if (!tableCards[cardview.sanitized]) {
          return true
        }
      }
    }
    return false
  }, [cardLists, tableCards])

  // Flattens cardlists into a single array of objects
  // Also adds card data and a key to each object
  const tableData: TableCardType[] = useMemo(() => {
    const newTableData = flatten(
      cardLists
        .filter((cardlist) => {
          for (const cardview of cardlist.cardviews) {
            if (cardview.cards) {
              if (cardview.cards.filter((card) => !tableCards[card.url]).length) return false
            } else if (!tableCards[cardview.sanitized]) {
              return false
            }
          }
          return true
        })
        .map((cardlist) => [
          { header: cardlist.header, url: cardlist.url },
          ...cardlist.cardviews.map((cardview) => {
            const tableCardView = {
              ...(cardview.cards ? tableCards[cardview.cards[0].url] : tableCards[cardview.sanitized]),
              ...cardview,
              key: v4(),
            }
            if (tableCardView.cards) {
              tableCardView.cards = tableCardView.cards.map((card) => ({ ...tableCards[card.url], ...card }))
            }
            return tableCardView
          }),
        ]),
    )
    return newTableData
  }, [cardLists, tableCards])

  useEffect(() => {
    if (!isRendered) {
      setIsRendered(true)
    }
  }, [])

  useEffect(() => {
    if (isRendered) {
      const names: string[] = []
      cardLists.forEach((cardlist) => {
        cardlist.cardviews.forEach((cardview) => {
          if (cardview.cards) cardview.cards.forEach((card) => names.push(card.url))
          else names.push(cardview.sanitized)
        })
      })
      cardService.getCardMap(names).then((cards) => {
        setTableCards(cards)
      })
    }
  }, [cardLists, isRendered])

  let columns: ColumnType[] = []
  const isDesktop = windowWidth >= desktopWidth
  const toggles = localStorageService.getJSON(isDesktop ? 'edhrec_table_toggles' : 'edhrec_table_toggles_mobile')

  if (tableData?.some((row) => row.rank)) {
    columns = [
      ...columns,
      {
        dataField: 'rank',
        headerStyle: { width: '59px' },
        sort: true,
        text: 'Rank',
      },
    ]
  }

  columns = [
    ...columns,
    {
      dataField: 'color_identity',
      formatter: colorIdentityFormatter,
      headerStyle: { width: '137px' },
      hidden: toggles ? !toggles.color_identity : !isDesktop,
      sort: true,
      text: 'Colors',
    },
    {
      dataField: 'cmc',
      formatter: cmcFormatter,
      headerStyle: { width: '58px' },
      hidden: toggles ? !toggles.cmc : !isDesktop,
      sort: true,
      sortFunc: reverseSort,
      text: 'CMC',
    },
    /* {
      dataField: "header",
      hidden: true,
      text: "Header",
    }, */
    {
      classes: styles.card,
      dataField: 'name',
      formatter: (col: any, row: TableCardType) => cardFormatter(col, row, labelVariant),
      sort: true,
      text: 'Name',
    },
    {
      dataField: 'rarity',
      formatter: rarityFormatter,
      headerStyle: { width: '61px' },
      hidden: toggles ? !toggles.rarity : true,
      sort: true,
      sortFunc: raritySort,
      text: 'Rarity',
    },
    {
      dataField: 'primary_type',
      headerStyle: { width: '116px' },
      hidden: toggles ? !toggles.primary_type : true,
      sort: true,
      text: 'Type',
    },
    {
      dataField: 'prices.cardkingdom.price',
      formatter: priceCKFormatter,
      headerStyle: { width: '136px' },
      hidden: toggles ? !toggles['prices.cardkingdom.price'] : !isDesktop,
      sort: true,
      sortFunc: reverseSort,
      text: 'Card Kingdom',
    },
    {
      dataField: 'prices.tcgplayer.price',
      formatter: priceTCGPFormatter,
      headerStyle: { width: '111px' },
      hidden: toggles ? !toggles['prices.tcgplayer.price'] : true,
      sort: true,
      sortFunc: reverseSort,
      text: 'TCGPlayer',
    },
    {
      dataField: 'prices.face2face.price',
      formatter: priceF2FFormatter,
      headerStyle: { width: '111px' },
      hidden: toggles ? !toggles['prices.face2face.price'] : true,
      sort: true,
      sortFunc: reverseSort,
      text: 'Face to Face',
    },
    {
      dataField: 'prices.cardmarket.price',
      formatter: priceCMFormatter,
      headerStyle: { width: '124px' },
      hidden: toggles ? !toggles['prices.cardmarket.price'] : true,
      sort: true,
      sortFunc: reverseSort,
      text: 'Cardmarket',
    },
    {
      dataField: 'prices.cardhoarder.price',
      formatter: priceCHFormatter,
      headerStyle: { width: '124px' },
      hidden: toggles ? !toggles['prices.cardhoarder.price'] : true,
      sort: true,
      sortFunc: reverseSort,
      text: 'Cardhoarder',
    },
    {
      dataField: 'salt',
      formatter: saltFormatter,
      headerStyle: { width: '51px' },
      hidden: toggles ? !toggles['salt'] : true,
      sort: true,
      sortFunc: reverseSort,
      text: 'Salt',
    },
  ]

  if (tableData) {
    if (tableData.some((row) => row.num_decks)) {
      columns = [
        ...columns,
        {
          dataField: 'num_decks',
          formatter: numDecksFormatter,
          headerStyle: { width: '68px' },
          sort: true,
          sortFunc: reverseSort,
          text: 'Decks',
        },
      ]
    }

    if (tableData.some((row) => row.potential_decks)) {
      columns = [
        ...columns,
        ...[
          {
            dataField: 'inclusion',
            formatter: inclusionFormatter,
            headerStyle: { width: '94px' },
            hidden: toggles ? !toggles['inclusion'] : !isDesktop,
            sort: true,
            sortFunc: (
              _a: TableCardType,
              _b: TableCardType,
              order: string,
              _dataField: string,
              rowA: TableCardType,
              rowB: TableCardType,
            ) => {
              if (order === 'asc') {
                return rowB.inclusion / rowB.potential_decks - rowA.inclusion / rowA.potential_decks
              }
              return rowA.inclusion / rowA.potential_decks - rowB.inclusion / rowB.potential_decks // desc
            },
            text: 'Inclusion',
          },
          {
            dataField: 'potential_decks',
            hidden: true,
            text: 'Potential Decks',
          },
        ],
      ]
    }

    if (tableData.some((row) => row.synergy)) {
      columns = [
        ...columns,
        {
          dataField: 'synergy',
          formatter: synergyFormatter,
          headerStyle: { width: '86px' },
          hidden: toggles ? !toggles['synergy'] : !isDesktop,
          sort: true,
          sortFunc: reverseSort,
          text: 'Synergy',
        },
      ]
    }

    if (tableData.some((row) => row.unpopularity)) {
      columns = [
        ...columns,
        {
          dataField: 'unpopularity',
          formatter: unpopularityFormatter,
          headerStyle: { width: '118px' },
          hidden: toggles ? !toggles['unpopularity'] : false,
          sort: true,
          text: 'Unpopularity',
        },
      ]
    }
  }

  const handleSelect = (row: TableCardType) => {
    if (
      row.is_partner
        ? row.cards.every(({ url }) => clipboardCards[url] === null)
        : clipboardCards[row.sanitized] === null || clipboardCards[row.url] === null
    ) {
      removeCard(row)
    } else addCard(row)
  }
  const selectedCards = tableData
    .filter((item) =>
      item.is_partner
        ? item.cards.every(({ url }) => clipboardCards[url] === null)
        : clipboardCards[item.sanitized] === null,
    )
    .map((item) => item.key)
  const handleSelectAll = (_isSelect: boolean, rows: TableCardType[]) => {
    if (rows.length && clipboardCards[rows[0].sanitized] === null) {
      const cards: any[] = []
      rows.forEach((item) => {
        if (item.is_partner) {
          item.cards.forEach((card) => cards.push(card))
        } else cards.push(item)
      })
      removeCard(cards)
    } else {
      addCard(rows)
    }
  }

  const selectRow: SelectRowProps<any> = {
    clickToSelect: true,
    headerColumnStyle: { width: '30px' },
    mode: 'checkbox',
    onSelect: (row: TableCardType, _isSelect: boolean, _rowIndex: number, e) => {
      // @ts-ignore
      if (e.target.nodeName === 'A') {
        return false
      }
      handleSelect(row)
      return true
    },
    onSelectAll: handleSelectAll,
    selectColumnStyle: { width: '30px' },
    selected: selectedCards,
    selectionHeaderRenderer: () => (
      <div className={styles.paperclip}>
        <Icon icon='paperclip' />
      </div>
    ),
  }

  // Currently just loads more for the first list it finds that has more
  const handleLoadMore = async () => {
    for (let i = 0; i < cardLists.length; i++) {
      if (cardLists[i].more) {
        update({ moreIndex: 0 })
        break
      }
    }
  }

  return (
    <div className={styles.table}>
      <ToolkitProvider columnToggle columns={columns} data={tableData} keyField='key' search>
        {({ baseProps, columnToggleProps, csvProps, searchProps }) => (
          <>
            <div className='d-flex justify-content-between mx-2 mb-2'>
              <div className='d-flex flex-column flex-sm-row gap-2'>
                {/*// @ts-ignore*/}
                <ToggleList {...columnToggleProps} />
                <ExportCSVButton {...csvProps} className='btn-primary'>
                  Export CSV
                </ExportCSVButton>
              </div>
              <SearchBar {...searchProps} />
            </div>

            <BootstrapTable
              {...baseProps}
              bodyClasses={classNames(styles.tableBody, 'edhrec-clipboard-dont-close')}
              bootstrap4
              defaultSortDirection='asc'
              headerWrapperClasses={classNames(styles.tableHead, 'edhrec-clipboard-dont-close')}
              hover
              selectRow={selectRow}
            />
            {isLoading && (
              <div className='align-items-center d-flex flex-column h-100 mt-5'>
                <Spinner size='xl' />
              </div>
            )}
            {Object.keys(tableCards).length > 0 && cardLists.some((cardlist) => cardlist.more) && (
              <span className={styles.loadMore}>
                <Button disabled={loading} onClick={handleLoadMore}>
                  {loading ? <Spinner /> : 'Load More'}
                </Button>
              </span>
            )}
          </>
        )}
      </ToolkitProvider>
    </div>
  )
}

export default memo(TableView)
